import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Checkbox, DataTable, Text } from 'react-native-paper';
import type { QuestionField } from './shareItemMapping';

const IOSCheckbox = Checkbox.IOS as any;

interface ItemLevelShareOptionsProps {
  includeRoom: boolean;
  includePhotos: boolean;
  includeFloor: boolean;
  onToggleRoom: () => void;
  onTogglePhotos: () => void;
  onToggleFloor: () => void;
}

interface QuestionCopyTableProps {
  fields: QuestionField[];
  questionsToCopy: Set<string>;
  onToggleCopy: (sourceQuestionId: string) => void;
  filterField?: (field: QuestionField) => boolean;
  selectColumnTitle?: string;
  uncheckedColor?: string;
}

export const ItemLevelShareOptions: React.FC<ItemLevelShareOptionsProps> = ({
  includeRoom,
  includePhotos,
  includeFloor,
  onToggleRoom,
  onTogglePhotos,
  onToggleFloor
}) => (
  <View style={styles.itemLevelOptions}>
    <View style={styles.itemOption}>
      <Checkbox.IOS
        status={includeRoom ? 'checked' : 'unchecked'}
        onPress={onToggleRoom}
        color="#4CAF50"
      />
      <Text style={styles.itemOptionLabel}>Include room selection</Text>
    </View>
    <View style={styles.itemOption}>
      <Checkbox.IOS
        status={includePhotos ? 'checked' : 'unchecked'}
        onPress={onTogglePhotos}
        color="#4CAF50"
      />
      <Text style={styles.itemOptionLabel}>Include photos</Text>
    </View>
    <View style={styles.itemOption}>
      <Checkbox.IOS
        status={includeFloor ? 'checked' : 'unchecked'}
        onPress={onToggleFloor}
        color="#4CAF50"
      />
      <Text style={styles.itemOptionLabel}>Include floor</Text>
    </View>
  </View>
);

export const QuestionCopyTable: React.FC<QuestionCopyTableProps> = ({
  fields,
  questionsToCopy,
  onToggleCopy,
  filterField,
  selectColumnTitle = '',
  uncheckedColor
}) => {
  const visibleFields = filterField ? fields.filter(filterField) : fields;

  return (
    <DataTable style={styles.table}>
      <DataTable.Header>
        <DataTable.Title style={styles.checkboxColumn}>{selectColumnTitle}</DataTable.Title>
        <DataTable.Title>Question</DataTable.Title>
        <DataTable.Title style={styles.typeColumn}>Type</DataTable.Title>
      </DataTable.Header>

      {visibleFields.map((sourceField) => {
        const isCopied = questionsToCopy.has(sourceField.id);

        return (
          <DataTable.Row key={sourceField.id}>
            <DataTable.Cell style={styles.checkboxColumn}>
              <IOSCheckbox
                status={isCopied ? 'checked' : 'unchecked'}
                onPress={() => onToggleCopy(sourceField.id)}
                color="#2196F3"
                uncheckedColor={uncheckedColor}
              />
            </DataTable.Cell>
            <DataTable.Cell>
              <Text style={styles.questionLabel}>{sourceField.label}</Text>
            </DataTable.Cell>
            <DataTable.Cell style={styles.typeColumn}>
              <Text style={styles.typeText}>{sourceField.type}</Text>
            </DataTable.Cell>
          </DataTable.Row>
        );
      })}
    </DataTable>
  );
};

const styles = StyleSheet.create({
  itemLevelOptions: {
    flexDirection: 'row',
    gap: 24,
    padding: 12,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#4CAF50'
  },
  itemOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  itemOptionLabel: {
    fontSize: 14,
    color: '#2E7D32',
    fontWeight: '500'
  },
  table: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4
  },
  checkboxColumn: {
    flex: 0.8,
    justifyContent: 'center',
    alignItems: 'center'
  },
  typeColumn: {
    flex: 1,
    justifyContent: 'center'
  },
  questionLabel: {
    fontSize: 13,
    color: '#333'
  },
  typeText: {
    fontSize: 11,
    color: '#666',
    fontStyle: 'italic'
  }
});
